import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './CustomSelect.scss';

/**
 * Custom Dropdown (single select)
 * 從 TicketCreateModal 抽出的共用元件
 *
 * @param {boolean} portal - 啟用 portal 模式：dropdown 渲染到 body，
 *   用 position:fixed 定位，可逃出 overflow:hidden/auto 的祖先元素裁切。
 */
const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = '請選擇...',
  renderOption,
  searchable = false,
  className = '',
  portal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [portalPos, setPortalPos] = useState(null);
  const ref = useRef(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // 點擊外部關閉
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // portal 模式要額外判斷 dropdown
        if (portal && dropdownRef.current?.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, portal]);

  // 計算 portal 定位
  const updatePortalPos = useCallback(() => {
    if (!portal || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 200 && rect.top > spaceBelow;
    const dropdownWidth = Math.max(rect.width, 200);
    // 靠右對齊：如果 dropdown 超出視窗右邊界就改用右對齊
    const overflowRight = rect.left + dropdownWidth > window.innerWidth;
    const horizontal = overflowRight
      ? { right: window.innerWidth - rect.right }
      : { left: rect.left };
    setPortalPos({
      position: 'fixed',
      minWidth: dropdownWidth,
      zIndex: 9500, // --z-dropdown-portal
      ...horizontal,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [portal]);

  useEffect(() => {
    if (open && portal) {
      updatePortalPos();
      window.addEventListener('scroll', updatePortalPos, true);
      window.addEventListener('resize', updatePortalPos);
      return () => {
        window.removeEventListener('scroll', updatePortalPos, true);
        window.removeEventListener('resize', updatePortalPos);
      };
    }
  }, [open, portal, updatePortalPos]);

  useEffect(() => {
    if (open && searchable) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  const selected = options.find((o) => o.value === value);

  const filtered =
    searchable && search.trim()
      ? options.filter((o) =>
          o.label.toLowerCase().includes(search.trim().toLowerCase())
        )
      : options;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="select-dropdown"
      style={portal && portalPos ? portalPos : undefined}
    >
      {searchable && (
        <div className="select-search">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#999" strokeWidth="1.5">
            <circle cx="6" cy="6" r="4" />
            <path d="M9.5 9.5l3 3" strokeLinecap="round" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋..."
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="select-empty">找不到符合的結果</div>
      ) : (
        filtered.map((opt) => (
          <div
            key={opt.value}
            className={`select-option ${value === opt.value ? 'selected' : ''}`}
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
          >
            {renderOption ? renderOption(opt) : opt.label}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className={`custom-select ${open ? 'open' : ''} ${className}`} ref={ref}>
      <button
        type="button"
        className="select-trigger"
        onClick={() => setOpen(!open)}
      >
        {selected ? (
          renderOption ? renderOption(selected) : <span>{selected.label}</span>
        ) : (
          <span className="select-placeholder">{placeholder}</span>
        )}
        <svg className="select-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (portal ? createPortal(dropdownContent, document.body) : dropdownContent)}
    </div>
  );
};

export default CustomSelect;
